/**
 * Client-side table configuration persistence using localStorage.
 * Persists filters, sort, and column state (width + order). Pagination is not persisted.
 */

const STORAGE_KEY_PREFIX = 'table-config-';

/** Minimal column state for persistence: colId + width + visibility. Array order = column order. */
export interface PersistedColumnState {
  colId: string;
  width?: number;
  hide?: boolean;
}

export interface PersistedTableState {
  filterModel?: Record<string, unknown> | null;
  /** Current sort: { field: colId, order: 'asc' | 'desc' } - from grid getColumnState() or getSortModel() */
  sort?: { field: string; order: string } | null;
  /** Column widths and order (array order = display order). */
  columnState?: PersistedColumnState[] | null;
}

/** True if persisted state includes filters or sort that should seed the first server query. */
export function hasAgGridRestorationPayload(state: PersistedTableState | null | undefined): boolean {
  if (!state || typeof state !== 'object') return false;
  return (
    (state.filterModel != null && Object.keys(state.filterModel as object).length > 0) ||
    Boolean(state.sort?.field && state.sort?.order)
  );
}

/** Persisted filters/sort for a table, or null when the first fetch should stay unfiltered. */
export function getPersistedTableQueryState(tableId: string | undefined): PersistedTableState | null {
  if (!tableId) return null;
  const saved = getTableState(tableId);
  return hasAgGridRestorationPayload(saved) ? saved : null;
}

export function persistedSortForApi(
  persisted: PersistedTableState | null | undefined,
  fieldMap?: Record<string, string>,
): { field: string; order: 'asc' | 'desc' } | null {
  const field = persisted?.sort?.field;
  const order = persisted?.sort?.order;
  if (!field || (order !== 'asc' && order !== 'desc')) return null;
  return { field: fieldMap?.[field] ?? field, order };
}

/** Seed a table sort ref from localStorage and return the persisted query state. */
export function applyPersistedSortToRef(
  tableId: string | undefined,
  sortRef: { current: { field: string | null; order: 'asc' | 'desc' | null } },
  fieldMap?: Record<string, string>,
): PersistedTableState | null {
  const persisted = getPersistedTableQueryState(tableId);
  const persistedSort = persistedSortForApi(persisted, fieldMap);
  if (persistedSort) sortRef.current = persistedSort;
  return persisted;
}

/**
 * Merges current sort into column defs so the grid shows the sort indicator when column defs
 * are updated (e.g. after data load). Use this when passing columnDefs to AG Grid so sort
 * is preserved across parent re-renders. Safe to call with any column def shape.
 */
export function applySortToColumnDefs(
  columnDefs: any[],
  sort: { field: string; order: string } | null | undefined
): any[] {
  if (!columnDefs?.length || !sort?.field || !sort?.order) return columnDefs ?? [];
  const order = sort.order === 'asc' || sort.order === 'desc' ? sort.order : undefined;
  if (!order) return columnDefs;
  return columnDefs.map((col: any) => {
    const colId = col.colId ?? col.field;
    if (colId === sort.field) return { ...col, sort: order, sortIndex: 0 };
    return col;
  });
}

function columnDefId(col: any): string | undefined {
  const id = col?.colId ?? col?.field;
  return id != null && id !== '' ? String(id) : undefined;
}

function isFlatColumnDefs(columnDefs: any[]): boolean {
  return columnDefs.every((d) => !d?.children?.length);
}

/** Apply persisted width / hide to a single column def (non-mutating). */
function mergePersistedIntoColDef(col: any, p: PersistedColumnState): any {
  const next = { ...col };
  if (typeof p.width === 'number' && p.width > 0) {
    next.width = p.width;
    next.flex = null;
    if ('initialFlex' in next) delete next.initialFlex;
  }
  if (typeof p.hide === 'boolean') next.hide = p.hide;
  return next;
}

function reorderFlatColumnDefs(columnDefs: any[], persisted: PersistedColumnState[]): any[] {
  const byId = new Map<string, any>();
  for (const col of columnDefs) {
    const id = columnDefId(col);
    if (id) byId.set(id, col);
  }
  const result: any[] = [];
  const used = new Set<string>();
  for (const p of persisted) {
    const col = byId.get(p.colId);
    if (col) {
      result.push(mergePersistedIntoColDef(col, p));
      used.add(p.colId);
    }
  }
  for (const col of columnDefs) {
    const id = columnDefId(col);
    if (id && !used.has(id)) result.push(col);
  }
  return result;
}

function applyPersistedToGroupedColumnDefs(
  columnDefs: any[],
  persistedById: Map<string, PersistedColumnState>
): any[] {
  return columnDefs.map((col) => {
    if (col?.children?.length) {
      return {
        ...col,
        children: applyPersistedToGroupedColumnDefs(col.children, persistedById),
      };
    }
    const id = columnDefId(col);
    const p = id ? persistedById.get(id) : undefined;
    if (!p) return col;
    return mergePersistedIntoColDef(col, p);
  });
}

export function applyPersistedColumnLayoutToColumnDefs(
  columnDefs: any[],
  tableId: string | undefined
): any[] {
  if (!columnDefs?.length || !tableId) return columnDefs ?? [];
  const saved = getTableState(tableId);
  const persisted = saved?.columnState;
  if (!persisted?.length) return columnDefs;

  if (isFlatColumnDefs(columnDefs)) return reorderFlatColumnDefs(columnDefs, persisted);

  const persistedById = new Map(persisted.map((p) => [p.colId, p]));
  return applyPersistedToGroupedColumnDefs(columnDefs, persistedById);
}

export function applyPersistedTableStateToColumnDefs(
  columnDefs: any[],
  tableId: string | undefined,
  sort: { field: string; order: string } | null | undefined
): any[] {
  const withLayout = applyPersistedColumnLayoutToColumnDefs(columnDefs, tableId);
  return applySortToColumnDefs(withLayout, sort);
}

function storageKey(tableId: string): string {
  return `${STORAGE_KEY_PREFIX}${tableId}`;
}

function getStorage(): Storage | null {
  if (typeof globalThis.window === 'undefined') return null;
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function getTableState(tableId: string): PersistedTableState | null {
  const storage = getStorage();
  if (!tableId || !storage) return null;
  try {
    const raw = storage.getItem(storageKey(tableId));
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as PersistedTableState;
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch {
    return null;
  }
}

export function setTableState(tableId: string, state: PersistedTableState): void {
  const storage = getStorage();
  if (!tableId || !storage) return;
  const hasContent =
    (state.filterModel != null && Object.keys(state.filterModel).length > 0) ||
    (state.sort?.field && state.sort?.order) ||
    (state.columnState != null && state.columnState.length > 0);
  if (!hasContent) return;
  try {
    storage.setItem(storageKey(tableId), JSON.stringify(state));
  } catch {
    // Ignore quota or other storage errors
  }
}


type GridApi = {
  getFilterModel?: () => Record<string, unknown>;
  setFilterModel?: (model: Record<string, unknown> | null) => void;
  getColumnState?: () => Array<{ colId?: string; sort?: string; width?: number; hide?: boolean }>;
  setColumnState?: (
    state: Array<{ colId: string; width?: number; hide?: boolean; flex?: number | null }>
  ) => void;
  getSortModel?: () => Array<{ colId: string; sort: string }>;
  setSortModel?: (model: Array<{ colId: string; sort: string }> | null) => void;
  sizeColumnsToFit?: () => void;
};

export function captureAgGridState(api: GridApi): PersistedTableState {
  const state: PersistedTableState = {};
  try {
    if (api.getFilterModel) {
      const model = api.getFilterModel();
      if (model && Object.keys(model).length > 0) state.filterModel = model;
    }
    const sortModel = api.getSortModel?.();
    if (sortModel && sortModel.length > 0 && sortModel[0].colId && sortModel[0].sort) {
      state.sort = { field: sortModel[0].colId, order: sortModel[0].sort };
    } else {
      const colState = api.getColumnState?.();
      const sortCol = colState?.find((c) => c.sort);
      if (sortCol?.colId && sortCol?.sort) {
        state.sort = { field: sortCol.colId, order: sortCol.sort };
      }
    }
    const colState = api.getColumnState?.();
    if (colState?.length) {
      const persisted: PersistedColumnState[] = colState
        .filter((c) => c.colId != null && c.colId !== '')
        .map((c) => ({
          colId: c.colId!,
          width: c.width,
          ...(typeof c.hide === 'boolean' ? { hide: c.hide } : {}),
        }));
      if (persisted.length > 0) state.columnState = persisted;
    }
  } catch {
    // Return partial state on error
  }
  return state;
}

const COLUMN_STATE_RESTORE_DELAY_MS = 150;

export function restoreAgGridState(api: GridApi, saved: PersistedTableState | null): void {
  if (!saved || !api) return;
  try {
    if (saved.filterModel != null && api.setFilterModel) {
      api.setFilterModel(saved.filterModel);
    }
    if (saved.sort?.field && saved.sort?.order) {
      api.setSortModel?.([{ colId: saved.sort.field, sort: saved.sort.order }]);
    }
    if (saved.columnState?.length && api.setColumnState) {
      const columnState = saved.columnState.map((c) => {
        const piece: { colId: string; width?: number; hide?: boolean; flex?: number | null } = {
          colId: c.colId,
        };
        if (typeof c.width === 'number' && c.width > 0) {
          piece.width = c.width;
          piece.flex = null;
        }
        if (typeof c.hide === 'boolean') piece.hide = c.hide;
        return piece;
      });
      setTimeout(() => {
        try {
          api.setColumnState?.(columnState);
        } catch {
          // ignore if grid unmounted
        }
      }, COLUMN_STATE_RESTORE_DELAY_MS);
    }
  } catch {
    // Fall back to default grid state on invalid/corrupted cache
  }
}
